import type { Action, Thunk, ThunkOn, Computed } from 'easy-peasy';
import { action, thunk, thunkOn, computed } from 'easy-peasy';
import type { StoreModel } from '..';
import { history } from '..';
import { NavigationBuilder } from './NavigationBuilder';
import type { ModalToggleParams, ModalState } from './types';
import { LOCATION_CHANGE } from 'redux-first-history';
import ReactGA from 'react-ga4';
import { shipiumNavigationBuilder } from './shipiumNavigationBuilder';
import { isShipiumEmail } from '~/utils/regex';
import { trackPageView } from '~/utils/analytics/userActionAnalytics';

export type ColumnWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
interface ColoumnWidthBreakpoint {
  columnWidth: ColumnWidth;
  maxSize: number;
}

const ColumnWidthBreakpoints: ColoumnWidthBreakpoint[] = [
  { columnWidth: 'xxl', maxSize: 1600 },
  { columnWidth: 'xl', maxSize: 1200 },
  { columnWidth: 'lg', maxSize: 992 },
  { columnWidth: 'md', maxSize: 768 },
  { columnWidth: 'sm', maxSize: 576 },
  { columnWidth: 'xs', maxSize: 0 },
];

export interface NavigationModel {
  onRouteChanged: ThunkOn<NavigationModel, void, StoreModel>;
  setActiveNavigation: Thunk<NavigationModel, string, void, StoreModel>;
  isNavigationCollapsed: boolean;
  isNavigationHidden: Computed<NavigationModel, boolean>;
  isNotificationPaneOpen: boolean;
  toggleNavigation: Action<NavigationModel, boolean>;
  toggleNotificationPane: Action<NavigationModel, boolean>;
  toggleModal: Action<NavigationModel, ModalToggleParams>;
  onAuthenticationSuccess: ThunkOn<NavigationModel, void, StoreModel>;
  onSecurityInfoFetch: ThunkOn<NavigationModel, void, StoreModel>;
  onLoadingFailure: ThunkOn<StoreModel, void, StoreModel>;
  onColumnWidthUpdate: ThunkOn<NavigationModel, ColumnWidth, StoreModel>;
  updateWindowSize: Action<NavigationModel, number>;
  pageTitle?: string;
  pageSubTitle?: string;
  modals: Record<string, ModalState>;
  columnWidth: ColumnWidth;
  columnWidthPixels: number;
  setPageTitle: Action<NavigationModel, string>;
}
export const flattenNavPath = (routes: any[]) => {
  const paths: any[] = [];
  const flatten = (r: any[]) => {
    r.forEach((val) => {
      if (val.links) {
        flatten(val.links);
      } else {
        paths.push(val);
      }
    });
  };
  flatten(routes);
  return paths.flat();
};

export const navigationModel: NavigationModel = {
  modals: {},
  columnWidth: 'lg',
  columnWidthPixels: window.innerWidth,
  isNavigationHidden: computed((state) => {
    return state.columnWidth === 'sm' || state.columnWidth === 'xs';
  }),
  onColumnWidthUpdate: thunkOn(
    (actions, storeActions) => actions.updateWindowSize,
    (actions, payload, { getState }) => {
      const state = getState();
      if (state.columnWidth === 'md' || state.columnWidth === 'sm' || state.columnWidth === 'xs') {
        // actions.toggleNavigation(true);
      } else if (state.isNavigationCollapsed) {
        //  actions.toggleNavigation(false);
      }
    },
  ),
  updateWindowSize: action((state, size) => {
    const columnWidth = ColumnWidthBreakpoints.find((breakpoint) => size >= breakpoint.maxSize)?.columnWidth || 'lg';
    state.columnWidthPixels = size;
    state.columnWidth = columnWidth;
  }),
  setActiveNavigation: thunk((actions, path, { getStoreState }) => {
    path = path || window.location.pathname;

    // Access all Redux state
    const globalState = getStoreState();

    const navigation = shipiumNavigationBuilder(
      globalState.session.user?.sessionPartnerUrl || 'shipium',
      globalState.session.user?.sessionPartnerRole || 'editor',
      isShipiumEmail(globalState.session.user?.email),
      globalState.partner.activePartner?.featureToggles || {},
      {},
    );

    const { link } = NavigationBuilder.getPageHeaderPropsForPath(navigation.build(), path);
    actions.setPageTitle('');

    if (link) {
      actions.setPageTitle(link.name);
    }
  }),
  // adding a manual page title set
  // this is useful for the case where we have a detail page that
  // has an ID param but we actually want to display a name
  // might need to rethink
  setPageTitle: action((state, payload) => {
    state.pageTitle = payload;
  }),
  onRouteChanged: thunkOn(
    (actions, storeActions) => LOCATION_CHANGE,
    (actions, payload, { getState, getStoreActions }) => {
      const state = getState();
      const path = payload.payload.location.pathname;
      const storeActions = getStoreActions();

      ReactGA.set({ page: path });
      ReactGA.send({
        path,
        hitType: 'pageview',
      });
      storeActions.session.verifyPartner();
      trackPageView(path);
      storeActions.session.fetchApplicationVersion();
      actions.setActiveNavigation(path);
      if (state.columnWidth === 'sm' || state.columnWidth === 'xs') {
        actions.toggleNavigation(true);
      }
    },
  ),
  toggleNavigation: action((state, collapsed) => {
    state.isNavigationCollapsed = collapsed;
  }),
  toggleNotificationPane: action((state, open) => {
    state.isNotificationPaneOpen = open;
  }),
  toggleModal: action((state, modalToggleParams) => {
    const { key } = modalToggleParams;
    state.modals[key] = modalToggleParams;
  }),
  onAuthenticationSuccess: thunkOn(
    (actions, storeActions) => [storeActions.session.switchActivePartner, storeActions.session.authenticationSuccess],
    (actions, payload, { getStoreState, getStoreActions }) => {
      const state = getStoreState();
      const storeActions = getStoreActions();
      if (!state.session.user) {
        return;
      }
      const { sessionPartnerId } = state.session.user;
      if (sessionPartnerId) {
        storeActions.partner.fetchPartner(sessionPartnerId);
      }

      actions.setActiveNavigation(window.location.pathname);
    },
  ),
  onSecurityInfoFetch: thunkOn(
    (action, storeActions) => [storeActions.partner.setActivePartner],
    (actions, payload, { getStoreState }) => {
      const state = getStoreState();

      if (!state.session.user) {
        return;
      }

      actions.setActiveNavigation(window.location.pathname);
    },
  ),
  onLoadingFailure: thunkOn(
    (actions, storeActions) => [storeActions.loading.loadingFailure],
    (state, payload, { getStoreState, getStoreActions }) => {
      const globalState = getStoreState();
      const actions = getStoreActions();
      actions.session.fetchApplicationVersion();
      const partnerUrl = globalState?.session?.user?.sessionPartnerUrl
        ? globalState?.session?.user?.sessionPartnerUrl
        : 'shipium';
      history.push(`/${partnerUrl}/loading-error`);
    },
  ),
  isNavigationCollapsed: false,
  isNotificationPaneOpen: false,
};
